// =============================================================================
// Library Assets Page — search/filter grid + table, stats, bulk actions
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Combobox,
  Input,
  Select,
  Spinner,
  Table,
} from '@tkpod/ui';
import type { Column } from '@tkpod/ui';
import {
  LibraryAssetStatus,
  LibraryMissingField,
  LibrarySearchField,
  LibrarySortOrder,
  LIBRARY_MAX_BULK_ASSETS,
  type LibraryAssetSummary,
} from '@tkpod/shared';
import { useDebounce } from '@/hooks/use-debounce';
import { toast } from '@/lib/toast';
import {
  fetchCategories,
  fetchStats,
  fetchTags,
  flattenCategories,
  libraryKeys,
  searchAssets,
} from './api';
import { LibraryBulkBar } from './LibraryBulkBar';
import { LibrarySingleUploadModal } from './LibrarySingleUploadModal';

/** Sentinel for the "Not assigned" category filter (categoryId IS NULL). */
const UNCATEGORIZED = '__none__';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...Object.values(LibraryAssetStatus).map((s) => ({ value: s, label: s })),
];
const SEARCH_IN_OPTIONS = [
  { value: LibrarySearchField.ALL, label: 'Everything' },
  { value: LibrarySearchField.SKU, label: 'SKU' },
  { value: LibrarySearchField.FILE_NAME, label: 'File name' },
  { value: LibrarySearchField.TITLE, label: 'Title' },
  { value: LibrarySearchField.DESCRIPTION, label: 'Description' },
];
const GALLERY_OPTIONS = [
  { value: '', label: 'Gallery: all' },
  { value: 'true', label: 'Gallery: visible' },
  { value: 'false', label: 'Gallery: hidden' },
];
/** Metadata-gap filter — composes with category/tags/status/search. */
const MISSING_OPTIONS = [
  { value: '', label: 'Missing: none' },
  { value: LibraryMissingField.TITLE, label: 'Missing title' },
  { value: LibraryMissingField.DESCRIPTION, label: 'Missing description' },
  { value: LibraryMissingField.KEYWORDS, label: 'Missing keywords' },
  { value: LibraryMissingField.TAGS, label: 'Missing tags' },
];
const SORT_OPTIONS = [
  { value: LibrarySortOrder.RECENT, label: 'Newest' },
  { value: LibrarySortOrder.POPULAR, label: 'Most used' },
];

function statusVariant(status: string): 'success' | 'warning' | 'danger' | 'default' {
  if (status === 'READY') return 'success';
  if (status === 'PROCESSING') return 'warning';
  if (status === 'FAILED') return 'danger';
  return 'default';
}

export function LibraryAssetsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [view, setView] = useState<'grid' | 'table'>('grid');
  const [rawQuery, setRawQuery] = useState('');
  const [searchIn, setSearchIn] = useState<string>(LibrarySearchField.ALL);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [status, setStatus] = useState('');
  const [gallery, setGallery] = useState('');
  const [missing, setMissing] = useState('');
  const [sort, setSort] = useState<LibrarySortOrder>(LibrarySortOrder.RECENT);
  const [flat, setFlat] = useState(false);
  const [page, setPage] = useState(1);
  // Selection is a Map of id → status so bulk actions (e.g. "Delete failed")
  // can inspect statuses even when the selection spans multiple pages.
  const [selected, setSelected] = useState<Map<string, string>>(new Map());
  const [selectingAll, setSelectingAll] = useState(false);
  const [quickUploadOpen, setQuickUploadOpen] = useState(false);

  const q = useDebounce(rawQuery, 300);

  const params = useMemo(
    () => ({ q, searchIn, categoryId, tagIds, status, gallery, missing, sort, flat, page }),
    [q, searchIn, categoryId, tagIds, status, gallery, missing, sort, flat, page],
  );

  /** Query string for the current filters at an arbitrary page/pageSize
   * (page 48 for display; pageSize 100 when collecting IDs for select-all). */
  const buildQs = useMemo(() => {
    return (pageNum: number, pageSize: number) => {
      const p = new URLSearchParams();
      if (q.trim()) p.set('q', q.trim());
      if (searchIn && searchIn !== LibrarySearchField.ALL) p.set('searchIn', searchIn);
      if (categoryId === UNCATEGORIZED) p.set('uncategorized', 'true');
      else if (categoryId) p.set('categoryId', categoryId);
      for (const t of tagIds) p.append('tagIds', t);
      if (status) p.set('status', status);
      if (gallery) p.set('galleryVisible', gallery);
      if (missing) p.set('missing', missing);
      if (sort !== LibrarySortOrder.RECENT) p.set('sort', sort);
      if (flat) p.set('flat', 'true');
      p.set('page', String(pageNum));
      p.set('pageSize', String(pageSize));
      return p.toString();
    };
  }, [q, searchIn, categoryId, tagIds, status, gallery, missing, sort, flat]);

  const qs = useMemo(() => buildQs(page, 48), [buildQs, page]);

  // Selection hygiene: a FILTER change invalidates the selection (acting on a
  // hidden, stale selection would be a silent foot-gun). Page changes keep it
  // so a selection can span pages; the bulk bar always shows the live count.
  useEffect(() => {
    setSelected(new Map());
  }, [buildQs]);

  const statsQuery = useQuery({ queryKey: libraryKeys.stats, queryFn: fetchStats });
  const categoriesQuery = useQuery({ queryKey: libraryKeys.categories, queryFn: fetchCategories });
  const tagsQuery = useQuery({ queryKey: libraryKeys.tags, queryFn: fetchTags });
  const assetsQuery = useQuery({
    queryKey: libraryKeys.assets(params),
    queryFn: () => searchAssets(qs),
  });

  const items = assetsQuery.data?.items ?? [];
  const total = assetsQuery.data?.total ?? 0;
  const pageSize = assetsQuery.data?.pageSize ?? 48;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const categoryOptions = useMemo(
    () => [
      { value: UNCATEGORIZED, label: 'Not assigned' },
      ...flattenCategories(categoriesQuery.data ?? []),
    ],
    [categoriesQuery.data],
  );
  const tagOptions = useMemo(
    () => (tagsQuery.data ?? []).map((t) => ({ value: t.id, label: t.name })),
    [tagsQuery.data],
  );

  const toggleSelect = (a: LibraryAssetSummary) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(a.id)) next.delete(a.id);
      else next.set(a.id, a.status);
      return next;
    });

  // "Select page" toggles only the current page's items; a selection made on
  // other pages is preserved (cleared automatically on any filter change).
  const allPageSelected = items.length > 0 && items.every((a) => selected.has(a.id));
  const togglePageSelection = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (allPageSelected) for (const a of items) next.delete(a.id);
      else for (const a of items) next.set(a.id, a.status);
      return next;
    });
  const hasFailedSelected = [...selected.values()].some((s) => s === 'FAILED');

  // Select every asset matching the current filters, across pages, capped at
  // the server-side bulk limit (LIBRARY_MAX_BULK_ASSETS).
  const selectAllMatching = async () => {
    if (selectingAll) return;
    setSelectingAll(true);
    try {
      const next = new Map<string, string>();
      let p = 1;
      for (;;) {
        const res = await searchAssets(buildQs(p, 100));
        for (const a of res.items) {
          if (next.size >= LIBRARY_MAX_BULK_ASSETS) break;
          next.set(a.id, a.status);
        }
        const fetched = p * (res.pageSize || 100);
        if (
          next.size >= LIBRARY_MAX_BULK_ASSETS ||
          fetched >= res.total ||
          res.items.length === 0
        ) {
          break;
        }
        p += 1;
      }
      setSelected(next);
      if (total > LIBRARY_MAX_BULK_ASSETS) {
        toast(
          `Selected the first ${LIBRARY_MAX_BULK_ASSETS} of ${total} assets (bulk action limit)`,
          'warning',
        );
      }
    } catch {
      toast('Failed to select all matching assets', 'error');
    } finally {
      setSelectingAll(false);
    }
  };

  const columns: Column<LibraryAssetSummary>[] = useMemo(
    () => [
      {
        key: 'select',
        header: (
          <input
            type="checkbox"
            checked={allPageSelected}
            onChange={togglePageSelection}
            aria-label="Select all on this page"
          />
        ),
        render: (a) => (
          <input
            type="checkbox"
            checked={selected.has(a.id)}
            onChange={() => toggleSelect(a)}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      {
        key: 'thumb',
        header: 'Preview',
        render: (a) =>
          a.thumbUrl ? (
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded border border-border bg-[#bdbdbd]">
              <img src={a.thumbUrl} alt={a.sku} className="max-h-full max-w-full object-contain" />
            </div>
          ) : (
            <div className="h-10 w-10 rounded border border-border bg-[#bdbdbd]" />
          ),
      },
      {
        key: 'sku',
        header: 'SKU',
        render: (a) => (
          <button
            type="button"
            onClick={() => navigate(`/library/assets/${a.id}`)}
            className="font-medium text-brand-400 hover:underline"
          >
            {a.sku}
          </button>
        ),
      },
      { key: 'fileName', header: 'File', render: (a) => <span className="text-text-secondary">{a.fileName}</span> },
      {
        key: 'status',
        header: 'Status',
        render: (a) => (
          <div className="flex items-center gap-1">
            <Badge variant={statusVariant(a.status)} size="sm">{a.status}</Badge>
            {a.variantCount ? <Badge variant="default" size="sm">{a.variantCount} var</Badge> : null}
            {!a.galleryVisible && <Badge variant="warning" size="sm">hidden</Badge>}
            {a.viaTagName && <Badge variant="primary" size="sm">via tag: {a.viaTagName}</Badge>}
            {a.usageCount ? <Badge variant="default" size="sm">used {a.usageCount}×</Badge> : null}
          </div>
        ),
      },
      { key: 'size', header: 'Size', render: (a) => <span className="text-text-tertiary">{a.widthPx}×{a.heightPx}</span> },
    ],
    [selected, allPageSelected, items, navigate],
  );

  return (
    <div className="space-y-6">
      {/* Stats cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {[
          { label: 'Total', value: statsQuery.data?.total },
          { label: 'Ready', value: statsQuery.data?.ready },
          { label: 'Processing', value: statsQuery.data?.processing },
          { label: 'Failed', value: statsQuery.data?.failed },
          { label: 'Archived', value: statsQuery.data?.archived },
          {
            label: 'Storage',
            value:
              statsQuery.data != null
                ? `${(statsQuery.data.storageBytes / 1024 / 1024).toFixed(1)} MB`
                : undefined,
          },
        ].map((c) => (
          <Card key={c.label}>
            <p className="text-xs text-text-tertiary">{c.label}</p>
            <p className="mt-1 text-xl font-semibold text-text-primary">{c.value ?? '—'}</p>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader
          title="Design Library"
          description="Search, filter and manage library assets."
          action={
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate('/library/uploads')}>
                Batches
              </Button>
              <Button variant="outline" size="sm" onClick={() => setQuickUploadOpen(true)}>
                Quick upload
              </Button>
              <Button size="sm" onClick={() => navigate('/library/upload')}>
                + Upload
              </Button>
            </div>
          }
        />

        {/* Filters */}
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-9">
          <div className="lg:col-span-2">
            <Input
              placeholder="Search…"
              value={rawQuery}
              onChange={(e) => { setRawQuery(e.target.value); setPage(1); }}
            />
          </div>
          <Select options={SEARCH_IN_OPTIONS} value={searchIn} onChange={(e) => { setSearchIn(e.target.value); setPage(1); }} />
          <Combobox
            options={categoryOptions}
            value={categoryId}
            onChange={(v) => { setCategoryId(v); setPage(1); }}
            placeholder="All categories"
            searchPlaceholder="Search categories…"
          />
          <Combobox
            multiple
            options={tagOptions}
            value={tagIds}
            onChange={(v) => { setTagIds(v); setPage(1); }}
            placeholder="All tags"
            searchPlaceholder="Search tags…"
          />
          <Select options={STATUS_OPTIONS} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} />
          <Select options={GALLERY_OPTIONS} value={gallery} onChange={(e) => { setGallery(e.target.value); setPage(1); }} />
          <Select options={MISSING_OPTIONS} value={missing} onChange={(e) => { setMissing(e.target.value); setPage(1); }} />
          <Select
            options={SORT_OPTIONS}
            value={sort}
            onChange={(e) => { setSort(e.target.value as LibrarySortOrder); setPage(1); }}
          />
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              <input type="checkbox" checked={flat} onChange={(e) => { setFlat(e.target.checked); setPage(1); }} />
              Flat list (show variants)
            </label>
            {items.length > 0 && (
              <label className="flex items-center gap-2 text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={togglePageSelection}
                />
                Select page ({items.length})
              </label>
            )}
            {total > items.length && (
              <Button
                variant="ghost"
                size="sm"
                loading={selectingAll}
                onClick={() => void selectAllMatching()}
              >
                Select all {Math.min(total, LIBRARY_MAX_BULK_ASSETS)} matching
              </Button>
            )}
          </div>
          <div className="flex gap-1">
            <Button variant={view === 'grid' ? 'primary' : 'ghost'} size="sm" onClick={() => setView('grid')}>Grid</Button>
            <Button variant={view === 'table' ? 'primary' : 'ghost'} size="sm" onClick={() => setView('table')}>Table</Button>
          </div>
        </div>

        {/* Results */}
        <div className="mt-4">
          {assetsQuery.isLoading ? (
            <div className="flex justify-center py-16"><Spinner size="lg" label="Loading assets…" /></div>
          ) : assetsQuery.isError ? (
            <p className="py-8 text-center text-sm text-red-400">Failed to load assets.</p>
          ) : view === 'table' ? (
            <Table<LibraryAssetSummary>
              columns={columns}
              data={items}
              rowKey={(a) => a.id}
              emptyMessage="No assets match your filters."
            />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {items.map((a) => (
                <div
                  key={a.id}
                  className={`group relative overflow-hidden rounded-lg border ${selected.has(a.id) ? 'border-brand-500' : 'border-brand-900'} bg-surface-secondary`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(a.id)}
                    onChange={() => toggleSelect(a)}
                    className="absolute left-2 top-2 z-10"
                  />
                  <button
                    type="button"
                    onClick={() => navigate(`/library/assets/${a.id}`)}
                    className="block w-full"
                  >
                    <div className="flex aspect-square items-center justify-center bg-[#bdbdbd] p-1">
                      {a.thumbUrl && <img src={a.thumbUrl} alt={a.sku} className="max-h-full max-w-full object-contain" />}
                    </div>
                    <div className="p-2 text-left">
                      <p className="truncate text-xs font-medium text-text-primary">{a.sku}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        <Badge variant={statusVariant(a.status)} size="sm">{a.status}</Badge>
                        {a.variantCount ? <Badge variant="default" size="sm">{a.variantCount}</Badge> : null}
                        {!a.galleryVisible && <Badge variant="warning" size="sm">hidden</Badge>}
                        {a.viaTagName && <Badge variant="primary" size="sm">via: {a.viaTagName}</Badge>}
                        {a.usageCount ? <Badge variant="default" size="sm">used {a.usageCount}×</Badge> : null}
                      </div>
                    </div>
                  </button>
                </div>
              ))}
              {items.length === 0 && <p className="col-span-full py-8 text-center text-sm text-text-tertiary">No assets match your filters.</p>}
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between text-sm text-text-secondary">
          <span>{total} asset(s)</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <span>Page {page} / {totalPages}</span>
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      </Card>

      {/* Bulk action bar (shared with batch review) */}
      <LibraryBulkBar
        selectedIds={[...selected.keys()]}
        categories={categoriesQuery.data ?? []}
        tags={tagsQuery.data ?? []}
        hasFailedSelected={hasFailedSelected}
        showArchive
        onDone={() => setSelected(new Map())}
      />

      {/* Quick single upload (batchless) — lands on the new asset's detail page */}
      <LibrarySingleUploadModal
        open={quickUploadOpen}
        onClose={() => setQuickUploadOpen(false)}
        onUploaded={(assetId) => {
          setQuickUploadOpen(false);
          void queryClient.invalidateQueries({ queryKey: ['library'] });
          navigate(`/library/assets/${assetId}`);
        }}
      />
    </div>
  );
}
